import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  createLocation,
  createProvider,
  editLocation,
  editProvider,
  loadProvider,
} from 'actions/provider';
import { loadUser } from 'actions/user';
import Button from 'atoms/button';
import Checkbox from 'atoms/checkbox';
import Dropdown from 'atoms/dropdown';
import Input from 'atoms/input';
import LocationSearch from 'atoms/location-search';
import {
  editLocationForm,
  initLocationForm,
} from 'store/reducers/locationForm';
import {
  editProviderForm,
  initProviderInfoForm,
} from 'store/reducers/providerForm';
import { formatWebsite } from 'util';

export default function BusinessInformationBlock() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const provider = useSelector((state) => state.provider);
  const {
    provider: providerMain,
    core,
    geo,
  } = useSelector((state) => state.main);
  const locationForm = useSelector((state) => state.locationForm);
  const providerForm = useSelector((state) => state.providerForm);
  const providerTypeOptions = providerMain.type;
  const animalTypesOptions = core.type;
  const { website, animalTypeIds, typeIds, phoneNumber, countryCodeId } =
    locationForm;
  const hasLocation = provider.locations?.length > 0;

  const canContinue = () => {
    const missingValues = [];
    Object.entries(locationForm).forEach(([key, value]) => {
      if (key === 'website' || key === 'neighborhood' || key === 'name') return;
      if (!value) missingValues.push(key);
    });

    return !(
      typeIds.length === 0 ||
      missingValues.length > 0 ||
      !animalTypeIds.length ||
      locationForm.address_1 === 'invalid_address' ||
      !providerForm.name
    );
  };

  useEffect(() => {
    if (provider.name) {
      dispatch(initProviderInfoForm({ name: provider.name }));
    }
    if (hasLocation) {
      dispatch(initLocationForm(provider.locations[0]));
    }
  }, []);

  const onNext = async (e) => {
    e.preventDefault();
    if (!canContinue()) return;

    // First we'll create/edit the provider using the name
    const providerAction = provider.name ? editProvider : createProvider;
    const providerCreated = await dispatch(
      providerAction({ name: providerForm.name })
    );
    if (providerCreated.success) {
      const providerId = providerCreated.data.data.uuid;
      dispatch(loadProvider(providerId));

      // Now we'll create/edit the location with the rest of the info
      const locationAction = hasLocation ? editLocation : createLocation;
      const body = {
        ...locationForm,
        name: providerForm.name,
        hours: [],
        countryCodeId: countryCodeId.id || countryCodeId.code.id,
        website: formatWebsite(website),
      };

      const locationCreated = await dispatch(locationAction(body, providerId));
      if (locationCreated.success) {
        // After a location is created, the user gets updated with the "role: owner" prop,
        // we need to fetch that
        dispatch(loadUser());
        const providerLoaded = await dispatch(loadProvider());
        if (providerLoaded.success) navigate('/signup-3');
      }
    }
  };

  return (
    <div className="business-info">
      <h1>Business information</h1>
      <form onSubmit={onNext}>
        <Input
          id="name"
          label="Business name"
          onChange={(value) =>
            dispatch(editProviderForm({ key: 'name', value }))
          }
          value={providerForm.name}
          autoFocus
        />
        <Dropdown
          id="providerType"
          label="Provider type"
          className="mt-35"
          placeholder="Boarder, Groomer, Veterinarian, etc"
          items={providerTypeOptions}
          onClick={({ id }) =>
            dispatch(editLocationForm({ key: 'typeIds', value: [id] }))
          }
          value={providerTypeOptions.find((el) => el.id === typeIds[0]) || ''}
        />
        <LocationSearch label="Address" editLocation={editLocationForm} />
        <div className="mt-30">
          <p className="bold f18 mb-15">Business phone number</p>
          <div className="flex-row">
            <Dropdown
              id="countryCode"
              className="country-code-input mt-0 mr-10"
              items={geo.countryCode}
              onClick={(value) =>
                dispatch(editLocationForm({ key: 'countryCodeId', value }))
              }
              value={countryCodeId}
            />
            <Input
              id="phone"
              className="w-100 mt-0"
              onChange={(value) =>
                dispatch(editLocationForm({ key: 'phoneNumber', value }))
              }
              value={phoneNumber}
              isNumber
            />
          </div>
        </div>
        <Input
          id="website"
          label="Website (optional)"
          onChange={(value) =>
            dispatch(editLocationForm({ key: 'website', value }))
          }
          value={locationForm.website}
        />
        <div className="flex-column mt-35 mb-0">
          <p className="mb-15 mt-10 bold f18">Animal cared for</p>
          <div className="flex-row flex-wrap">
            {animalTypesOptions.map((el) => (
              <div key={el.value} className="checkbox-container__checkbox">
                <Checkbox
                  size="small"
                  id={el.id}
                  className="mr-25"
                  value={animalTypeIds.includes(el.id)}
                  onClick={({ id }) => {
                    const ids = [...animalTypeIds];
                    if (ids.includes(id)) {
                      const index = ids.indexOf(id);
                      ids.splice(index, 1);
                    } else {
                      ids.push(id);
                    }
                    dispatch(
                      editLocationForm({ key: 'animalTypeIds', value: ids })
                    );
                  }}
                  text={el.value}
                />
              </div>
            ))}
          </div>
        </div>
        <Button
          className="mt-32 mb-30"
          type="submit"
          text="Next"
          variant={canContinue() ? 'default' : 'disabled'}
        />
      </form>
    </div>
  );
}
